/** @jsxImportSource @emotion/react */
import { useState, useEffect, useMemo, ChangeEvent, memo, Dispatch } from 'react';
import { useTranslation } from 'react-i18next';
import { GlobalBtnGroup, SubTitleGroup, SubTitleLayout } from 'components/layouts/ContentLayout';
import { useLoading } from 'components/process/Loading';
import { useMessageBar } from 'hooks/useMessageBar';
import {
  Button,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { IconButton } from 'components/buttons/IconSVG';
import { tb } from 'components/layouts/Table';
import _ from 'lodash';
import { useCommonModal } from 'hooks/useCommonModal';
import { DefaultProps } from 'pages/md/invest/MdIvesCmpuRegPage';
import { st } from 'components/inputs/CustomInput';
import ToggleButton from 'components/buttons/ToggleButton';
import {
  findMdIvesDtlIsptList,
  mergeMdIvesDtlIspt,
  saveMdIvesDtlIsptList,
  saveMdIvesDtlIsptListRow,
  updateMdIvesDtlIsptListByArticle,
} from 'apis/md/MdProject';
import { MdIvesDtlIspt, MdIvesMdlSmly } from 'models/md/MdIves';
import DtlIsptInqPopup from 'pages/md/popup/DtlIsptInqPopup';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import UprcInqPopup from 'pages/md/popup/UprcInqPopup';
import { CrudCode } from 'models/common/Edit';
import { CommonUtil } from 'utils/CommonUtil';
import { LightBlueColor } from 'ui/theme/Color';
import { resetInvestResult } from 'apis/md/MdInvestResult';
import DtlIsptAddRowPopup from 'pages/md/popup/DtlIsptAddRowPopup';
import MultiUprcInqPopup from 'pages/md/popup/MultiUprcInqPopup';
import { MdLine } from 'models/md/MdLine';

type PopupType = 'DETAIL' | 'ADDROW' | 'COST' | null; // 상세 점검사항 | 행추가 | 단가 검색

interface DtlIsptProps extends DefaultProps {
  selectedSmlyModel?: MdIvesMdlSmly;
  isInit?: any;
  setIsInit: Dispatch<any>;
}
interface MdIvesDtlIsptByUI extends MdIvesDtlIspt {
  checked: boolean;
}
interface PopupProps {
  isShow: PopupType;
  selectedItem?: any;
}

const DtlIsptMatr = ({
  elmPrjId,
  ivesMdlId,
  isInit,
  setIsInit,
  selectedSmlyModel,
  isEditable,
  isRegStat,
}: DtlIsptProps) => {
  const { t } = useTranslation();
  const { isLoading, openLoading } = useLoading();
  const { openMessageBar } = useMessageBar();
  const { openCommonModal } = useCommonModal();

  const [activePopup, setActivePopup] = useState<PopupProps>({ isShow: null });
  const [nestedRowData, setNestedRowData] = useState<any>([]);

  const layoutDefinition = [
    { header: String(t('md.label.공정', '공정')), width: 120 },
    { header: String(t('md.label.설비군', '설비군')), width: 150 },
    { header: String(t('md.label.비표준항목', '비표준항목')), width: 180 },
    { header: String(t('md.label.투자 상세', '투자 상세')), width: 220 },
    { header: String(t('md.label.점검항목', '점검항목')), width: 160 },
    { header: String(t('md.label.단가', '단가')), width: 100 },
    { header: String(t('md.label.수량 기준', '수량 기준')), width: '*' },
    { header: String(t('md.label.단가 기준', '단가 기준')), width: 100 },
    { header: String(t('md.label.Ref 모델', 'Ref 모델')), width: 110 },
    { header: String(t('md.label.WBS', 'WBS')), width: 110 },
    { header: String(t('md.label.반영여부', '반영여부')), width: 80 },
  ];

  const flattedData: MdIvesDtlIspt[] = useMemo(() => {
    return _.flattenDeep(nestedRowData.map((proc) => proc.eqclGroups.map((eqcl) => eqcl.items)));
  }, [nestedRowData]);

  const isModify = () => {
    return !!ivesMdlId && !ivesMdlId.includes('_NEW_') && !!elmPrjId;
  };

  const initCodes = () => {
    if (!elmPrjId || !ivesMdlId || ivesMdlId.includes('_NEW_')) {
      setNestedRowData([]);
      return;
    }

    if (!isModify()) return;

    findMdIvesDtlIsptList({ elmPrjId, ivesMdlId }).then((res) => {
      const nestedObj = (res || []).reduce((acc, item: MdIvesDtlIspt) => {
        const prdnProcCd = item.prdnProcCd || 'UNDEFINED';
        const eqclId = item.eqclId || 'UNDEFINED';

        // 1. 공정(Process) 레벨 초기화
        if (!acc[prdnProcCd]) {
          acc[prdnProcCd] = {
            depth: 1,
            prdnProcCd,
            prdnProcCdNm: item.prdnProcCdNm,
            isShow: true,
            checked: false,
            eqclGroups: {},
          };
        }

        // 2. 설비군(Equipment Group) 레벨 초기화
        if (!acc[prdnProcCd].eqclGroups[eqclId]) {
          acc[prdnProcCd].eqclGroups[eqclId] = {
            depth: 2,
            prdnProcCd,
            eqclId,
            eqclNm: item.eqclNm,
            isShow: true,
            checked: false,
            items: [],
          };
        }

        // 3. 투자항목 데이터 삽입
        acc[prdnProcCd].eqclGroups[eqclId].items.push({
          ...item,
          prevQuotUprc: item.quotUprc,
          checked: false,
        });

        return acc;
      }, {});
      const nested = Object.values(nestedObj).map((proc: any) => {
        return {
          ...proc,
          eqclGroups: Object.values(proc.eqclGroups).map((group: any) => {
            return {
              ...group,
              items: group.items,
            };
          }),
        };
      });
      setNestedRowData(nested);
    });
  };

  const btnAdd = () => {
    // 투자항목 지정 체크 필요
    if (!isModify()) {
      openMessageBar({
        type: 'error',
        content: t('com.msg.모델을 등록해주세요.', '모델을 등록해주세요.'),
      });
      return;
    }

    setActivePopup({ isShow: 'DETAIL' });
  };

  const btnAddRow = () => {
    // 투자항목 지정 체크 필요
    if (!isModify()) {
      openMessageBar({
        type: 'error',
        content: t('com.msg.모델을 등록해주세요.', '모델을 등록해주세요.'),
      });
      return;
    }

    setActivePopup({ isShow: 'ADDROW' });
  };

  const btnDel = () => {
    const flatted: any = _.flattenDeep(
      nestedRowData.map((proc) => proc.eqclGroups.map((eqcl) => eqcl.items))
    );

    const selected = flatted.filter((o) => o.checked).map((o) => o.mdlDtlIsptId);
    if (!selected?.length) {
      openMessageBar({
        type: 'error',
        content: t('com.msg.선택된 데이터가 없습니다.', '선택된 데이터가 없습니다.'),
      });
      return;
    }

    openCommonModal({
      modalType: 'confirm',
      content: t(
        'com.msg.항목 삭제 시 투자항목에서도 삭제됩니다.<br/>해당 항목을 삭제하시겠습니까?',
        '항목 삭제 시 투자항목에서도 삭제됩니다.<br/>해당 항목을 삭제하시겠습니까?'
      ),
      yesCallback: () => {
        const delData = flatted
          .filter((o) => selected.includes(o.mdlDtlIsptId))
          .map((o) => ({ ...o, crudKey: CrudCode.DELETE }));
        openLoading(true);
        saveMdIvesDtlIsptList(delData)
          .then((res) => {
            if (res.successOrNot === 'Y') {
              openMessageBar({
                type: 'confirm',
                content: t('com.msg.삭제되었습니다.', '삭제되었습니다.'),
              });
              setIsInit((prev) => !prev);

              // 투자항목 결과 분석 초기화
              resetInvestResult(elmPrjId, ivesMdlId);
            } else {
              openMessageBar({
                type: 'error',
                content: t('com.msg.삭제에 실패하였습니다.', '삭제에 실패하였습니다.'),
              });
            }
          })
          .catch((err) => {
            openMessageBar({
              type: 'error',
              content: t('com.msg.삭제 중 오류가 발생했습니다.', '삭제 중 오류가 발생했습니다.'),
            });
          })
          .finally(() => openLoading(false));
      },
    });
  };

  const isValid = () => {
    return flattedData.some((o) => !!o.quotUprc);
  };

  const btnSave = () => {
    if (!isValid()) {
      openMessageBar({
        type: 'error',
        content: t(
          'com.msg.입력된 단가가 없습니다. 단가 입력 후 저장해주세요',
          '입력된 단가가 없습니다. 단가 입력 후 저장해주세요'
        ),
      });
      return;
    }
    openCommonModal({
      modalType: 'confirm',
      content: t(`com.msg.변경사항을 저장하시겠습니까?`, `변경사항을 저장하시겠습니까?`),
      yesCallback: async () => {
        openLoading(true);
        saveMdIvesDtlIsptList(flattedData)
          .then(async (res) => {
            if (res.successOrNot === 'Y') {
              openMessageBar({
                type: 'confirm',
                content: t('com.msg.저장 되었습니다.', '저장 되었습니다.'),
              });

              // 저장버튼 클릭으로 저장 하는 경우 : 상세 점검사항 -> 투자항목으로 항목 추가 후 투자항목 데이터 다시 조회
              await updateMdIvesDtlIsptListByArticle(elmPrjId, ivesMdlId ?? '');
              setIsInit((prev) => !prev);

              // 투자항목 결과 분석 초기화
              await resetInvestResult(elmPrjId, ivesMdlId);
            } else {
              openMessageBar({
                type: 'error',
                content: res.data || t('com.msg.저장에 실패하였습니다.', '저장에 실패하였습니다.'),
              });
            }
          })
          .catch((err) => {
            openMessageBar({
              type: 'error',
              content: t('com.msg.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
            });
          })
          .finally(() => openLoading(false));
      },
    });
  };

  const btnChkAll = (e: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setNestedRowData((prev) =>
      prev.map((proc) => ({
        ...proc,
        checked,
        eqclGroups: proc.eqclGroups.map((eqcl) => ({
          ...eqcl,
          checked,
          items: eqcl.items.map((o) => ({ ...o, checked })),
        })),
      }))
    );
  };

  const btnChk = (checked: boolean, item: any) => {
    if (item.depth === 1) {
      //
      setNestedRowData((prev) =>
        prev.map((proc) => {
          if (proc.prdnProcCd === item.prdnProcCd) {
            return {
              ...proc,
              checked,
              eqclGroups: proc.eqclGroups.map((eqcl) => ({
                ...eqcl,
                checked,
                items: eqcl.items.map((o) => ({ ...o, checked })),
              })),
            };
          } else return { ...proc };
        })
      );
    } else if (item.depth === 2) {
      setNestedRowData((prev) =>
        prev.map((proc) => {
          if (proc.prdnProcCd === item.prdnProcCd) {
            return {
              ...proc,
              eqclGroups: proc.eqclGroups.map((eqcl) => {
                if (eqcl.eqclId === item.eqclId) {
                  return { ...eqcl, checked, items: eqcl.items.map((o) => ({ ...o, checked })) };
                } else return { ...eqcl };
              }),
            };
          } else return { ...proc };
        })
      );
    } else {
      setNestedRowData((prev) =>
        prev.map((proc) => {
          if (proc.prdnProcCd === item.prdnProcCd) {
            return {
              ...proc,
              eqclGroups: proc.eqclGroups.map((eqcl) => {
                if (eqcl.eqclId === item.eqclId) {
                  return {
                    ...eqcl,
                    items: eqcl.items.map((o) => {
                      if (o.mdlDtlIsptId === item.mdlDtlIsptId) {
                        return { ...o, checked };
                      } else return { ...o };
                    }),
                  };
                } else return { ...eqcl };
              }),
            };
          } else return { ...proc };
        })
      );
    }
  };

  const onToggleRow = (item) => {
    // 공정 Depth인 경우
    if (item.depth === 1) {
      setNestedRowData((prev) =>
        prev.map((o) => {
          return o === item ? { ...o, isShow: !o.isShow } : o;
        })
      );
    }
    // 설비군 Depth인 경우
    else {
      setNestedRowData((prev) =>
        prev.map((o) => ({
          ...o,
          eqclGroups: o.eqclGroups.map((x) => {
            return x === item ? { ...x, isShow: !x.isShow } : x;
          }),
        }))
      );
    }
  };

  const handleOnChange = (item, key, value) => {
    setNestedRowData((prev) =>
      prev.map((prdn) => {
        if (prdn.prdnProcCd === item.prdnProcCd) {
          return {
            ...prdn,
            eqclGroups: prdn.eqclGroups.map((eqcl) => {
              if (eqcl.eqclId === item.eqclId) {
                return {
                  ...eqcl,
                  items: eqcl.items.map((o) => {
                    if (o.mdlDtlIsptId === item.mdlDtlIsptId) {
                      if (key === 'quotUprc') {
                        return {
                          ...o,
                          [key]: value,
                          uprcHdfgInsYn: value != o.prevQuotUprc ? 'Y' : 'N',
                          crudKey: CrudCode.UPDATE,
                        };
                      } else {
                        return { ...o, [key]: value, crudKey: CrudCode.UPDATE };
                      }
                    } else return o;
                  }),
                };
              } else return eqcl;
            }),
          };
        } else return prdn;
      })
    );
  };
  const handleOnChangeObj = async (item, obj) => {
    const reqData = [
      {
        ...item,
        ...obj,
        crudKey: CrudCode.UPDATE,
      },
    ];
    const result = await saveMdIvesDtlIsptList(reqData);
    if (result.successOrNot === 'Y') initCodes();
  };

  const getIvesItems = () => {
    return _.uniqBy(
      flattedData.map((o) => ({
        // 투자비 검색 조건
        stndYn: o.stndYn,
        elmPrjId: o.elmPrjId,
        ivesMdlId: o.ivesMdlId,
        uprcDcnTpCd: o.uprcDcnTpCd,
        ivesAtcNm: o.dtlIsptNm,
        // UI 매핑용
        mdlDtlIsptId: o.mdlDtlIsptId,
        prdnProcCd: o.prdnProcCd,
        prdnProcCdNm: o.prdnProcCdNm,
        eqclId: o.eqclId,
        eqclNm: o.eqclNm,
        dtlIsptId: o.dtlIsptId,
        dtlIsptNm: o.dtlIsptNm,
        selectYn: o.mdRltDtlId ? 'Y' : o.uprcHdfgInsYn === 'Y' ? 'H' : 'N',
        child: flattedData
          .filter(
            (x) =>
              x.prdnProcCd === o.prdnProcCd && x.eqclId === o.eqclId && x.dtlIsptId === o.dtlIsptId
          )
          .map((x) => ({ ...x, crudKey: undefined })),
      })),
      (o) => `${o.prdnProcCd}${o.eqclId}${o.dtlIsptId}`
    );
  };

  // 상세 점검사랑 추가 callback function
  const dtlIsptCallbackFn = async (selectedItems) => {
    /**
     * 1. 추가한 상세 점검사항을 저장한다.
     * 2. merge 쿼리를 활용해 유사모델의 투자이력을 매핑한다.
     * 3. reload를 통해 데이터를 다시 조회한다.
     */
    const flattedData = _.flattenDeep(
      nestedRowData.map((proc) => proc.eqclGroups.map((eqcl) => eqcl.items))
    );
    openLoading(true);
    const reqData = _.uniqBy(
      [
        ...flattedData,
        ...selectedItems.map((o) => ({
          ...o,
          elmPrjId,
          ivesMdlId,
          crudKey: CrudCode.CREATE,
        })),
      ],
      (o) => (o.prdnProcCd ?? '') + (o.eqclId ?? '') + (o.dtlIsptId ?? '')
    );
    const result = await saveMdIvesDtlIsptList(reqData);
    if (result.successOrNot === 'Y') {
      const result2 = await mergeMdIvesDtlIspt(
        elmPrjId,
        ivesMdlId ?? '',
        selectedSmlyModel?.mdlId ?? ''
      );
      if (result2.successOrNot === 'Y') {
        initCodes();
      } else {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.투자이력(단가) 데이터 조회 중 오류가 발생하였습니다.',
            '투자이력(단가) 데이터 조회 중 오류가 발생하였습니다.'
          ),
        });
      }
    } else {
      openMessageBar({
        type: 'error',
        content: t(
          'com.msg.상세 점검사항 저장 중 오류가 발생하였습니다.',
          '상세 점검사항 저장 중 오류가 발생하였습니다.'
        ),
      });
    }
    openLoading(false);
  };

  // 행추가 팝업 callback function
  const dtlIsptAddRowCallbackFn = async (addItems) => {
    const reqData = addItems.map((o) => ({ ...o, elmPrjId, ivesMdlId }));
    openLoading(true);
    const result = await saveMdIvesDtlIsptListRow(reqData);
    if (result.successOrNot === 'Y') {
      setActivePopup({ isShow: null });
      initCodes();
    } else {
      openMessageBar({
        type: 'error',
        content:
          result.data ||
          t('com.msg.저장 중 오류가 발생하였습니다.', '저장 중 오류가 발생하였습니다.'),
      });
    }
    openLoading(false);
  };

  // 투자항목 Mapping 팝업 callback function
  const multiUprcInqCallbackFn = async (items) => {
    openLoading(true);
    const result = await saveMdIvesDtlIsptList(items);
    if (result.successOrNot === 'Y') {
      // 저장버튼 클릭으로 저장 하는 경우 : 상세 점검사항 -> 투자항목으로 항목 추가 후 투자항목 데이터 다시 조회
      const r1 = await updateMdIvesDtlIsptListByArticle(elmPrjId, ivesMdlId ?? '');
      setIsInit((prev) => !prev);

      // 투자항목 결과 분석 초기화
      const r2 = await resetInvestResult(elmPrjId, ivesMdlId);

      if (r1.successOrNot !== 'Y') {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.투자항목에 반영 중 오류가 발생했습니다.',
            '투자항목에 반영 중 오류가 발생했습니다.'
          ),
        });
      } else if (r2.successOrNot !== 'Y') {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.투자항목 결과 분석 초기화 중 오류가 발생했습니다.',
            '투자항목 결과 분석 초기화 중 오류가 발생했습니다.'
          ),
        });
      } else {
        openMessageBar({
          type: 'confirm',
          content: t('com.msg.저장되었습니다.', '저장되었습니다.'),
        });
      }
    } else {
      openMessageBar({
        type: 'error',
        content: t(
          'com.msg.상세 점검사항 저장 중 오류가 발생하였습니다.',
          '상세 점검사항 저장 중 오류가 발생하였습니다.'
        ),
      });
    }
    setActivePopup({ isShow: null });
    openLoading(false);
  };

  useEffect(() => {
    initCodes();
  }, [elmPrjId, ivesMdlId, isInit]);

  return (
    <>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('md.label.상세 점검사항', '상세 점검사항')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(flattedData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <GlobalBtnGroup>
          <label className="text-sm mr-2">{t('md.label.단가(억원)', '단가(억원)')}</label>
          {isEditable && (
            <>
              <Button
                css={IconButton.button}
                className="icoNone ok"
                disabled={!isModify() || !isRegStat}
                onClick={btnAdd}
              >
                {t('com.label.상세 점검사항 추가', '상세 점검사항 추가')}
              </Button>
              <Button
                css={IconButton.button}
                className="addRow"
                disabled={!isModify() || !isRegStat}
                onClick={btnAddRow}
              >
                {t('com.label.행추가', '행추가')}
              </Button>
              <Button
                css={IconButton.button}
                className="delRow"
                disabled={!isModify() || !isRegStat}
                onClick={btnDel}
              >
                {t('com.label.행삭제', '행삭제')}
              </Button>
              <Button
                css={IconButton.button}
                className="save"
                disabled={!isModify() || !isRegStat || isLoading}
                onClick={btnSave}
              >
                {t('com.label.상세 점검사항 저장', '상세 점검사항 저장')}
              </Button>
            </>
          )}
        </GlobalBtnGroup>
      </SubTitleLayout>
      <TableContainer css={tb.tableCol}>
        <Table>
          <TableHead>
            <TableRow>
              {isEditable && (
                <TableCell width={43}>
                  <Checkbox
                    css={st.checkbox}
                    className="custom"
                    disabled={!isModify()}
                    onChange={(e, c) => btnChkAll(e, c)}
                  />
                </TableCell>
              )}
              {layoutDefinition.map((item, idx) => {
                return (
                  <TableCell key={idx} width={item.width}>
                    {item.header}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {nestedRowData.map((prdn: any, idx: number) => {
              return (
                <>
                  <TableRow className="bg-cyan-100">
                    {isEditable && (
                      <TableCell width={43}>
                        <Checkbox
                          css={st.checkbox}
                          className="custom"
                          checked={prdn.checked}
                          disabled={!isModify()}
                          onChange={(e, c) => btnChk(c, prdn)}
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <ToggleButton
                        text={prdn.prdnProcCdNm}
                        initialOpen={prdn.isShow}
                        onToggle={() => onToggleRow(prdn)}
                      />
                    </TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                  {prdn.isShow &&
                    prdn.eqclGroups.map((eqcl: any, idx2: number) => {
                      return (
                        <>
                          <TableRow key={idx2} className="bg-violet-100">
                            {isEditable && (
                              <TableCell width={43}>
                                <Checkbox
                                  css={st.checkbox}
                                  className="custom"
                                  checked={eqcl.checked}
                                  disabled={!isModify()}
                                  onChange={(e, c) => btnChk(c, eqcl)}
                                />
                              </TableCell>
                            )}
                            <TableCell></TableCell>
                            <TableCell>
                              <ToggleButton
                                text={eqcl.eqclNm}
                                initialOpen={eqcl.isShow}
                                onToggle={() => onToggleRow(eqcl)}
                              />
                            </TableCell>
                            <TableCell></TableCell>
                            <TableCell></TableCell>
                            <TableCell></TableCell>
                            <TableCell></TableCell>
                            <TableCell></TableCell>
                            <TableCell></TableCell>
                            <TableCell></TableCell>
                            <TableCell></TableCell>
                            <TableCell></TableCell>
                          </TableRow>
                          {eqcl.isShow &&
                            eqcl.items.map((item: MdIvesDtlIsptByUI, idx3: number) => {
                              return (
                                <TableRow key={idx3} className="hover:bg-gray-100">
                                  {isEditable && (
                                    <TableCell width={43}>
                                      <Checkbox
                                        css={st.checkbox}
                                        className="custom"
                                        checked={item.checked}
                                        disabled={!isModify()}
                                        onChange={(e, c) => btnChk(c, item)}
                                      />
                                    </TableCell>
                                  )}
                                  <TableCell>{/* 공정 */}</TableCell>
                                  <TableCell>{/* 설비군 */}</TableCell>
                                  <TableCell className="left">{item.dtlIsptNm}</TableCell>
                                  <TableCell className="left">{item.ivesDtlDesc}</TableCell>
                                  <TableCell className="left">
                                    {isEditable ? (
                                      <CustomInputWithSearch
                                        name="rsnCtn"
                                        value={item.rsnCtn}
                                        onChange={(e) =>
                                          handleOnChange(item, 'rsnCtn', e.target.value)
                                        }
                                      />
                                    ) : (
                                      <span>{item.rsnCtn}</span>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {isEditable ? (
                                      <CustomInputWithSearch
                                        name="quotUprc"
                                        className={`find suffix`}
                                        style={
                                          item.uprcHdfgInsYn === 'Y'
                                            ? {
                                                borderColor: LightBlueColor.LightBlue700,
                                                backgroundColor: LightBlueColor.LightBlue100,
                                              }
                                            : {}
                                        }
                                        value={item.quotUprc}
                                        autoComplete="off"
                                        onSearchClick={(e) =>
                                          setActivePopup({
                                            isShow: 'COST',
                                            selectedItem: item,
                                          })
                                        }
                                        onChange={(e) =>
                                          handleOnChange(
                                            item,
                                            'quotUprc',
                                            CommonUtil.onlyNumInput(e.target.value, 5)
                                          )
                                        }
                                      />
                                    ) : (
                                      <span>{item.quotUprc}</span>
                                    )}
                                  </TableCell>
                                  <TableCell>{item.calLogcCtn}</TableCell>
                                  <TableCell>{item.uprcDcnCtn}</TableCell>
                                  <TableCell>{item.tgtMdlNm}</TableCell>
                                  <TableCell>{item.wbsCd}</TableCell>
                                  <TableCell>
                                    {item.ivesAtcYn === 'Y'
                                      ? String(t('md.label.반영', '반영'))
                                      : String(t('md.label.미반영', '미반영'))}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                        </>
                      );
                    })}
                </>
              );
            })}
            {!nestedRowData?.length && (
              <TableRow>
                <TableCell colSpan={layoutDefinition.length}>
                  조회 가능한 데이터가 없습니다.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {activePopup.isShow === 'DETAIL' && ( // 상세 점검사항 추가
        <DtlIsptInqPopup
          open={activePopup.isShow === 'DETAIL'}
          close={() => setActivePopup({ isShow: null })}
          ids={
            flattedData?.map((o: any) => `${o.prdnProcCd}_${o.eqclId}_${o.dtlIsptId}` || '') || []
          }
          callbackFn={dtlIsptCallbackFn}
        />
      )}
      {activePopup.isShow === 'ADDROW' && ( // 행추가
        <DtlIsptAddRowPopup
          open={activePopup.isShow === 'ADDROW'}
          close={() => setActivePopup({ isShow: null })}
          callbackFn={dtlIsptAddRowCallbackFn}
        />
      )}
      {activePopup.isShow === 'COST' && ( // 단가 검색
        <MultiUprcInqPopup
          open={activePopup.isShow === 'COST'}
          close={() => setActivePopup({ isShow: null })}
          gbn="DTL"
          ivesItems={getIvesItems()}
          selectedItem={activePopup.selectedItem}
          callbackFn={multiUprcInqCallbackFn}
        />
      )}
    </>
  );
};

export default DtlIsptMatr;
